/**
 * KEY2 Pricing Library
 * --------------------
 * Identical math to v1 (verified against Phase5Decision data, 81 units).
 * All prices in OMR.
 *
 * VAT:               5%   (applied to base price)
 * Cash discount:     3%   (off price+VAT when paying cash)
 * Down payment:      20%  (of price+VAT)
 * Monthly plan:      40 months
 * Quarterly plan:    13 quarters
 * Flex plan:         3 months at OMR 300 each, then 36 months equal,
 *                    plus a final settlement month
 *
 * Phase 8f-bcs (2026-05-07): the rent / yield / payback calculations
 * that previously took a `monthlyRent` *assumption* and derived
 * gross/net yield + payback have been REMOVED. The operator's Sales
 * Master Sheet now provides three pre-computed scenarios per unit
 * (low/mid/high) — see RentScenario in types.ts. Use
 * getRentScenario / getMidScenario to read those values; never
 * recompute yield from a rent guess.
 */

import projectMeta from '@data/project.json';

import type { RentScenario, Unit } from './types';

const { vatRate, cashDiscountRate, downPaymentRate, monthly, quarterly, flex } =
  projectMeta.paymentPlans;

export type PaymentPlanType = 'cash' | 'monthly' | 'quarterly' | 'flex';

export interface PriceBreakdown {
  basePrice: number;
  vat: number;
  priceWithVat: number;
  cashDiscount: number;
  cashFinal: number;
  downPayment: number;
  monthlyInst: number;
  quarterlyInst: number;
  flex: {
    downPayment: number;
    initialMonthly: number;     // OMR 300/mo for first 3 months
    initialMonths: number;      // 3
    midMonthly: number;         // months 4-39 same as monthly plan
    settlementMonth: number;    // final month catches the remainder
  };
}

/** Compute every payment plan for a given base price. */
export function calculatePrice(basePrice: number): PriceBreakdown {
  const vat = round(basePrice * vatRate);
  const priceWithVat = basePrice + vat;
  const cashDiscount = round(basePrice * cashDiscountRate);
  const cashFinal = round(priceWithVat - cashDiscount);
  const downPayment = round(priceWithVat * downPaymentRate);
  const remainder = priceWithVat - downPayment;

  const monthlyInst = round(remainder / monthly.months);
  const quarterlyInst = round(remainder / quarterly.quarters);

  const flexInitialTotal = flex.initialMonths * flex.initialMonthly;
  const flexMidMonths = monthly.months - flex.initialMonths - 1;
  const flexMidMonthly = monthlyInst;
  const flexSettlement = round(remainder - flexInitialTotal - flexMidMonths * flexMidMonthly);

  return {
    basePrice,
    vat,
    priceWithVat,
    cashDiscount,
    cashFinal,
    downPayment,
    monthlyInst,
    quarterlyInst,
    flex: {
      downPayment,
      initialMonthly: flex.initialMonthly,
      initialMonths: flex.initialMonths,
      midMonthly: flexMidMonthly,
      settlementMonth: flexSettlement,
    },
  };
}

/* ---------------------------------------------------------------- */
/* Rent / yield — read pre-computed scenarios from the unit catalog.  */
/* ---------------------------------------------------------------- */

export type RentScenarioKey = 'low' | 'mid' | 'high';

/** Return the requested rent scenario for a unit. */
export function getRentScenario(unit: Unit, scenario: RentScenarioKey): RentScenario {
  return unit.rentScenarios[scenario];
}

/** Default display scenario — the operator-curated "متوسط". */
export function getMidScenario(unit: Unit): RentScenario {
  return unit.rentScenarios.mid;
}

/* ---------------------------------------------------------------- */
/* Back-compat shim                                                   */
/* ---------------------------------------------------------------- */
/**
 * Legacy alias — older modules (sessionStore, ShareModal, api.ts) carry
 * a `yieldData` field whose shape used to be `YieldBreakdown`. They now
 * carry a `RentScenario`. Exporting the alias avoids a churn of type
 * imports across consumers that don't actually need the legacy fields.
 */
export type YieldBreakdown = RentScenario;

/* ---------------------------------------------------------------- */
/* Formatting helpers                                                 */
/* ---------------------------------------------------------------- */

/** Round to 2 decimal places (OMR is divided into 1000 baisa, but display is 2 decimals). */
function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Format a number as OMR with locale-aware separators. */
export function formatOMR(n: number, opts: { withSymbol?: boolean; decimals?: number } = {}): string {
  const { withSymbol = true, decimals = 0 } = opts;
  const formatted = n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return withSymbol ? `${formatted} ر.ع` : formatted;
}

/** Format a percentage from a *decimal* (0.117 → "11.70%"). */
export function formatPercent(decimal: number, decimals: number = 2): string {
  return `${(decimal * 100).toFixed(decimals)}%`;
}

/**
 * Format an already-percent value (e.g. yieldPct from RentScenario,
 * stored as 14.11 not 0.1411). Drops the trailing decimal when the
 * value is a clean integer (12.0 → "12%", 14.11 → "14.1%").
 */
export function formatPercentValue(percent: number): string {
  if (Number.isInteger(percent)) return `${percent}%`;
  return `${percent.toFixed(1)}%`;
}
